// Home's "Gepland met vrienden" card: the one session that is live now or comes next, from the
// sessions hub. A tap opens the tasting itself when this device holds it, else the session. Only an
// account has a hub, so a signed-out visitor, or nothing planned, shows nothing at all.
import { useEffect, useState } from "react";
import { useT } from "../lib/i18n";
import { navigate } from "../lib/router";
import { useSession } from "../lib/social/auth";
import { nextPlanned } from "../lib/social/plannedCard";
import { listSessions, type SessionSummary } from "../lib/social/sessionsApi";
import { useRun } from "../lib/tasting/store";
import { Avatar, Card, Caption, Column, Icon, ListRow, Section, Status, Subheading } from "../ui";
import { initialsOf } from "../lib/tasting/matrix";

export function PlannedSessionCard(): React.JSX.Element | null {
  const { t, locale } = useT();
  const auth = useSession();
  const run = useRun();
  const signedIn = auth.status === "signedIn";
  const [session, setSession] = useState<SessionSummary | undefined>(undefined);

  useEffect(() => {
    if (!signedIn) {
      setSession(undefined);
      return undefined;
    }
    let cancelled = false;
    Promise.all([listSessions("live"), listSessions("upcoming")])
      .then(([live, upcoming]) => {
        if (!cancelled) setSession(nextPlanned(live.items, upcoming.items));
        return undefined;
      })
      // The card is a shortcut: when the hub cannot be read the rest of home is still right.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  if (session === undefined) return null;
  const live = session.status === "live";
  const when = session.scheduledAt === null ? undefined : new Date(session.scheduledAt);
  const heldHere = live && run?.sessionId === session.id;
  const open = (): void => {
    if (heldHere)
      navigate(run.started ? `/tasting/wine/${String(run.current + 1)}` : "/tasting/lobby");
    else navigate(`/session/${encodeURIComponent(session.id)}`);
  };
  const going = t("together.planned.going", { n: session.goingCount });
  const time = when?.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });

  return (
    <Section title={t("together.planned.eyebrow")} data-testid="planned-card">
      <Card flush>
        <ListRow
          testId={`planned-${session.id}`}
          title={session.name}
          subtitle={[going, time].filter((part) => part !== undefined).join(" · ")}
          ariaLabel={t("together.planned.open", { name: session.name })}
          leading={
            live || when === undefined ? (
              <Icon name="play" size={22} tone="ready" />
            ) : (
              <Column alignItems="center" minWidth={36}>
                <Subheading render="p">{String(when.getDate())}</Subheading>
                <Caption>{when.toLocaleDateString(locale, { month: "short" })}</Caption>
              </Column>
            )
          }
          trailing={
            live ? (
              <Status tone="ok">{t("together.planned.live")}</Status>
            ) : (
              <Avatar
                size="row"
                initials={initialsOf(session.host.displayName)}
                src={session.host.avatarUrl ?? undefined}
              />
            )
          }
          onPress={open}
        />
      </Card>
    </Section>
  );
}
